import {
  getMappedCategories,
  productCategoriesMapping,
  stagingCategoriesMapping,
} from '@ouster/webviz-react-components';

import LZString from 'lz-string';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { parseUrlParams } from '../../utils/driveUtil';

type GroupFilterItem = {
  [key: string]: string[];
};

const isProduction = window.location.hostname === 'studio.ouster.com';

const categoryMapping = isProduction
  ? productCategoriesMapping
  : stagingCategoriesMapping;

const useFilters = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Memoize filters to avoid recomputation on every render
  const filters = useMemo(
    () => parseUrlParams(location.search),
    [location.search]
  );

  // Function to compress and update URL params
  const updateFiltersInUrl = (
    updatedFilters: Record<string, string[]>,
    currentOrderBy?: string
  ) => {
    const compressedFilters = LZString.compressToEncodedURIComponent(
      JSON.stringify(updatedFilters)
    );

    const params = new URLSearchParams();
    params.set('filters', compressedFilters);
    if (currentOrderBy) {
      params.set('order_by', currentOrderBy);
    }

    navigate({ search: params.toString() });
  };

  // Set individual filter
  const setFilter = (key: string, values: string[]) => {
    const currentOrderBy =
      new URLSearchParams(location.search).get('order_by') ?? undefined;

    const updatedFilters = {
      ...filters,
      [key]: values,
    };
    updateFiltersInUrl(updatedFilters, currentOrderBy);
  };

  const decompressExistedFilter = (
    currentCompressedFilters: string | null
  ): Record<string, string[]> => {
    if (currentCompressedFilters) {
      try {
        const decompressedFilters = LZString.decompressFromEncodedURIComponent(
          currentCompressedFilters
        );
        if (decompressedFilters) {
          return JSON.parse(decompressedFilters) as Record<string, string[]>;
        }
        return {};
      } catch (error) {
        console.error('Error decompressing filters:', error);
        return {};
      }
    }
    return {};
  };

  const setGroupFilters = (groupFilterItems: GroupFilterItem[]) => {
    // Here we need to preserve previous filters
    const params = new URLSearchParams(location.search);

    const currentOrderBy = params.get('order_by') ?? undefined;
    const currentCompressedFilters = params.get('filters');

    const currentFilters: Record<string, string[]> = {};

    // Decompress existing filters if available
    const prevFilters: Record<string, string[]> = decompressExistedFilter(
      currentCompressedFilters
    );

    // Append new group filters
    groupFilterItems.forEach((groupFilterItem) => {
      const [key] = Object.keys(groupFilterItem);
      const values = groupFilterItem[key];
      if (!currentFilters[key]) {
        currentFilters[key] = [];
      }
      currentFilters[key] = [...values];
    });

    /* 
      we are using short list of category but it needs to be mapped to full list
      for filtering. staging and product have the different mapping, we need to do it accordingly.
    */

    Object.keys(currentFilters).forEach((key) => {
      if (key === 'data_category') {
        const mappedCategories: string[] = [];

        currentFilters[key].forEach((cate) => {
          const categories = getMappedCategories(categoryMapping, cate);

          mappedCategories.push(...categories);
        });

        currentFilters[key] = [...mappedCategories];
      }
    });

    const newFilters = { ...prevFilters, ...currentFilters };

    updateFiltersInUrl(newFilters, currentOrderBy);
  };

  return {
    filters,
    setFilter,
    setGroupFilters,
  };
};

export default useFilters;
